// MPEG-TS keeps all elementary streams on the wire. Browser demuxers select
// only one, so advertise the user's audio PID in the PMT before demuxing.
// Media/PCR/PTS bytes are never changed; packet count and offsets stay intact.
const PACKET = 188;

export function crc32Mpeg(bytes) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte << 24;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x80000000 ? (crc << 1) ^ 0x04c11db7 : crc << 1;
    }
  }
  return crc >>> 0;
}

function parsePmt(section) {
  if (section[0] !== 2 || section.length < 16 || !(section[5] & 1) || crc32Mpeg(section) !== 0)
    return null;
  const start = 12 + (((section[10] & 15) << 8) | section[11]);
  const end = section.length - 4;
  if (start > end) return null;
  const entries = [];
  const duplicates = new Map();
  for (let offset = start; offset < end; ) {
    if (offset + 5 > end) return null;
    const size = 5 + (((section[offset + 3] & 15) << 8) | section[offset + 4]);
    if (offset + size > end) return null;
    const type = section[offset];
    let audio = [3, 4, 15, 17, 129, 135].includes(type);
    let language = '';
    let role = 0;
    for (let d = offset + 5; d + 2 <= offset + size; ) {
      const tag = section[d];
      const length = section[d + 1];
      if (d + 2 + length > offset + size) return null;
      if (tag === 10 && length >= 4) {
        language = String.fromCharCode(...section.slice(d + 2, d + 5))
          .replace(/[^a-z]/gi, '')
          .toLowerCase();
        role = section[d + 5];
      }
      if (
        type === 6 &&
        ([0x6a, 0x7a, 0x7c].includes(tag) ||
          (tag === 5 &&
            ['AC-3', 'EAC3', 'Opus'].includes(
              String.fromCharCode(...section.slice(d + 2, d + 2 + length))
            )))
      )
        audio = true;
      d += 2 + length;
    }
    const key = `${language}:${type}:${role}`;
    const ordinal = duplicates.get(key) || 0;
    if (audio) duplicates.set(key, ordinal + 1);
    const pid = ((section[offset + 1] & 31) << 8) | section[offset + 2];
    entries.push({offset, size, audio, pid, id: `${key}:${ordinal}`, language, role});
    offset += size;
  }
  return {start, entries};
}

export class TsAudioSelector {
  constructor(preferred = () => 'auto', onTracks = () => {}) {
    this.preferred = preferred;
    this.onTracks = onTracks;
    this.tail = new Uint8Array();
    this.pending = null;
    this.mode = null;
    this.pmtPid = null;
  }

  push(data, final = false) {
    const input = new Uint8Array(data);
    const bytes = new Uint8Array(this.tail.length + input.length);
    bytes.set(this.tail);
    bytes.set(input, this.tail.length);
    this.tail = new Uint8Array();
    if (this.mode === null) {
      if (bytes.length < PACKET * 3 && !final) {
        this.tail = bytes;
        return new Uint8Array();
      }
      // Other supported containers (FLV/fMP4) pass through unchanged.
      this.mode =
        bytes[0] === 0x47 && (bytes.length <= PACKET || bytes[PACKET] === 0x47) ? 'ts' : 'other';
    }
    if (this.mode === 'other') return bytes;
    const output = [];
    for (let offset = 0; offset + PACKET <= bytes.length; offset += PACKET) {
      const packet = bytes.slice(offset, offset + PACKET);
      const pid = ((packet[1] & 31) << 8) | packet[2];
      const control = (packet[3] >> 4) & 3;
      let start = control & 2 ? 5 + packet[4] : 4;
      const hasPayload =
        !!(control & 1) && start < PACKET && packet[0] === 0x47 && !(packet[1] & 0x80);
      const begins = !!(packet[1] & 0x40);
      if (
        this.pending &&
        (this.pending.packets.length >= 64 || (pid === this.pending.pid && begins))
      ) {
        output.push(...this.pending.packets);
        this.pending = null;
      }
      if (hasPayload && begins) {
        start += 1 + packet[start];
        // PAT anchors the program rather than mistaking PES data for a PMT.
        if (pid === 0 && packet[start] === 0 && start + 12 <= PACKET) {
          const end = Math.min(
            PACKET,
            start + 3 + (((packet[start + 1] & 15) << 8) | packet[start + 2]) - 4
          );
          for (let p = start + 8; p + 4 <= end; p += 4) {
            if (packet[p] || packet[p + 1]) {
              this.pmtPid = ((packet[p + 2] & 31) << 8) | packet[p + 3];
              break;
            }
          }
        }
        if (!this.pending && pid === this.pmtPid && packet[start] === 2 && start + 3 <= PACKET) {
          const length = 3 + (((packet[start + 1] & 15) << 8) | packet[start + 2]);
          if (length >= 16 && length <= 1024)
            this.pending = {pid, length, packets: [], refs: [], bytes: [], cc: null};
        }
      }
      const pending = this.pending;
      if (!pending) {
        output.push(packet);
        continue;
      }
      pending.packets.push(packet);
      if (pid === pending.pid && hasPayload) {
        const cc = packet[3] & 15;
        if (pending.cc !== null && cc !== ((pending.cc + 1) & 15)) {
          output.push(...pending.packets);
          this.pending = null;
          continue;
        }
        pending.cc = cc;
        for (let p = start; p < PACKET && pending.bytes.length < pending.length; p += 1) {
          pending.bytes.push(packet[p]);
          pending.refs.push([packet, p]);
        }
        if (pending.bytes.length === pending.length) {
          this.rewrite(pending);
          output.push(...pending.packets);
          this.pending = null;
        }
      }
    }
    this.tail = bytes.slice(bytes.length - (bytes.length % PACKET));
    if (final) {
      if (this.pending) output.push(...this.pending.packets);
      this.pending = null;
      output.push(this.tail);
      this.tail = new Uint8Array();
    }
    const result = new Uint8Array(output.reduce((sum, chunk) => sum + chunk.length, 0));
    let offset = 0;
    for (const chunk of output) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }

  rewrite(pending) {
    const section = new Uint8Array(pending.bytes);
    const parsed = parsePmt(section);
    if (!parsed) return;
    const tracks = parsed.entries.filter((entry) => entry.audio);
    if (!tracks.length) return; // Recovery slates must not erase a user's preference.
    const chosen = tracks.find((track) => track.id === this.preferred()) || tracks[0];
    this.onTracks(
      tracks.map((track, index) => ({
        id: track.id,
        label: `${track.language ? track.language.toUpperCase() : 'Audio'} · ${index + 1}${
          track.role === 3 ? ' · Audiodescripción' : ''
        }`
      })),
      chosen.id
    );
    if (tracks.length < 2) return;
    const result = Array.from(section.slice(0, parsed.start));
    for (const entry of parsed.entries) {
      if (!entry.audio || entry.pid === chosen.pid)
        result.push(...section.slice(entry.offset, entry.offset + entry.size));
    }
    const length = result.length + 4 - 3;
    result[1] = (result[1] & 0xf0) | (length >> 8);
    result[2] = length & 255;
    const crc = crc32Mpeg(result);
    result.push((crc >>> 24) & 255, (crc >>> 16) & 255, (crc >>> 8) & 255, crc & 255);
    pending.refs.forEach(([packet, offset], index) => {
      packet[offset] = index < result.length ? result[index] : 255;
    });
  }
}
